import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import slugify from 'slugify';
import { PaperClipIcon, XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { TASK_TYPE_LABELS, AR_NOT_FOR_AGENT_TASK_TYPES } from '@claimup/shared/constants/assignments';
import { MANUALLY_SETTABLE_STATES, expedientStatusLabel } from '@claimup/shared/constants/expedient';
import {
  MAX_EMAIL_ATTACHMENTS_BYTES,
  mensajeDemasiadoGrande,
} from '@claimup/shared/constants/email-attachments';
import api, { getErrorMessage } from '../../../services/api';
import { uploadFileToS3 } from '../../../services/s3Service';
import SelectorDocumentosExpediente from '../../../components/SelectorDocumentosExpediente';

/**
 * «Lo hago yo»: el caso no se aprueba y lo hace la persona desde aquí.
 *
 * Decisión de Edgar (08/10/2026): no hay segundas vueltas del agente. O se
 * aprueba a la primera, o se hace a mano en el mismo sitio, con las acciones de
 * la página de gestión. Cada acción queda anotada en el plan
 * (`/manual-actions`) y es lo que aprende la memoria del agente.
 *
 * El WhatsApp sale por la línea de la conversación —por el brazo si es del
 * navegador—, cosa que la página de gestión no hace: lo decide el backend.
 *
 * El SMS sale por BulkGate desde el número de sistema, que es el único que
 * recibe la respuesta del deudor. Tope de 160 caracteres, el mismo del agente.
 */

/** Lo que cabe en un SMS. Más, y se parte en varios que se cobran por separado. */
const MAX_SMS = 160;

/** Las tareas que se pueden crear desde aquí, las de siempre. */
const TIPOS_DE_TAREA = [
  'follow_up_whatsapp',
  'follow_up_email',
  'follow_up_sms',
  'payment_verification',
  'follow_up',
  'follow_up_call',
  'new_whatsapp',
];

/** Lo que cuenta como «alguien volverá a mirar este expediente». */
const SIGUIENTES = [
  'follow_up_whatsapp',
  'follow_up_email',
  'follow_up_sms',
  'payment_verification',
  'follow_up',
  'reply_whatsapp',
  'reply_email',
  'reply_sms',
];

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const diasHasta = fechaISO =>
  Math.max(0, Math.round((Date.parse(`${fechaISO}T00:00:00`) - Date.parse(`${hoyISO()}T00:00:00`)) / 86400000));

/** Lo que acepta Gmail en total por correo. */

const escapeHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** El cuerpo se escribe en texto y la ruta lo manda como HTML: sin esto, se pierden los saltos de línea. */
const textoAHtml = texto =>
  texto
    .trim()
    .split(/\n{2,}/)
    .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

/** Sube un fichero a S3, dentro de la carpeta del expediente, como el chat de email. */
const subirArchivo = async (file, expedientId, carpeta) => {
  const punto = file.name.lastIndexOf('.');
  const base = punto > 0 ? file.name.slice(0, punto) : file.name;
  const extension = punto > 0 ? file.name.slice(punto) : '';
  const slug = `${carpeta}/${Date.now()}-${slugify(base, { lower: true, strict: true })}${extension}`;
  const { s3Key } = await uploadFileToS3(file, expedientId, slug, file.name, file.type);
  return { filename: file.name, mime_type: file.type || 'application/octet-stream', size: file.size, s3_key: s3Key };
};

/** Botón de adjuntar y la lista de lo adjuntado. */
const Adjuntos = ({ archivos, onAnadir, onQuitar, multiple = false, disabled, testId }) => {
  const input = useRef(null);
  return (
    <div className='mt-2'>
      {archivos.length > 0 && (
        <ul className='mb-1 space-y-1'>
          {archivos.map((a, i) => (
            <li
              key={`${a.s3_key}-${i}`}
              className='flex items-center justify-between gap-2 text-xs text-slate-700 bg-slate-50 rounded-lg px-2 py-1'
            >
              <span className='truncate'>{a.filename}</span>
              <button
                type='button'
                onClick={() => onQuitar(i)}
                aria-label={`Quitar ${a.filename}`}
                className='text-slate-500 hover:text-red-600'
              >
                <XMarkIcon className='w-4 h-4' />
              </button>
            </li>
          ))}
        </ul>
      )}
      {(multiple || archivos.length === 0) && (
        <button
          type='button'
          disabled={disabled}
          onClick={() => input.current?.click()}
          className='inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50'
        >
          <PaperClipIcon className='w-4 h-4' />
          Adjuntar archivo
        </button>
      )}
      <input
        ref={input}
        type='file'
        multiple={multiple}
        className='hidden'
        data-testid={testId}
        onChange={e => {
          const files = Array.from(e.target.files || []);
          e.target.value = '';
          if (files.length) onAnadir(files);
        }}
      />
    </div>
  );
};

const QUE_HICE = {
  send_whatsapp: p => `WhatsApp: «${p.message}»${p.attachment ? ` + ${p.attachment.filename}` : ''}`,
  send_sms: p => `SMS: «${p.message}»`,
  send_email: p => `Email: «${p.subject}»${p.attachments?.length ? ` + ${p.attachments.length} adjunto(s)` : ''}`,
  update_expedient_status: p => `Estado → ${expedientStatusLabel(p.new_status)}`,
  create_annotation: p => `${p.es_tarea ? 'Tarea al cliente' : 'Nota'}: «${p.text}»`,
  create_follow_up_assignment: p =>
    `Tarea ${TASK_TYPE_LABELS[p.task_type] || p.task_type} ${p.days_from_now === 0 ? 'para hoy' : `a ${p.days_from_now} día(s)`}`,
  cancel_assignment: () => 'Tarea cancelada',
  complete_annotation_task: () => 'Anotación-tarea completada',
  attach_document: p => `Documento subido: ${p.file_name}`,
};

const Bloque = ({ titulo, children }) => (
  <div className='rounded-lg border border-slate-200 bg-white p-3'>
    <p className='text-xs font-semibold text-slate-500 uppercase mb-2'>{titulo}</p>
    {children}
  </div>
);

const AIAgentLoHagoYo = ({ plan, contexto, onCambio, onTerminado, basePath = '/ai-agent' }) => {
  const [hecho, setHecho] = useState([]);
  const [ocupado, setOcupado] = useState(false);

  const [whatsapp, setWhatsapp] = useState('');
  const [sms, setSms] = useState('');
  const [adjuntoWhatsapp, setAdjuntoWhatsapp] = useState([]);
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [adjuntosEmail, setAdjuntosEmail] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [verDocumentos, setVerDocumentos] = useState(false);
  const [estado, setEstado] = useState('');
  const [nota, setNota] = useState('');
  const [esTarea, setEsTarea] = useState(false);
  const [tipoTarea, setTipoTarea] = useState('follow_up_whatsapp');
  // La revisión del cliente es siempre de AR: ahí el agente ni llama ni lleva SMS.
  const tiposDeTarea =
    basePath === '/ar-review' ? TIPOS_DE_TAREA.filter(t => !AR_NOT_FOR_AGENT_TASK_TYPES.includes(t)) : TIPOS_DE_TAREA;
  const [fechaTarea, setFechaTarea] = useState(hoyISO());
  const [descTarea, setDescTarea] = useState('');
  const [porque, setPorque] = useState('');

  const expedientId = contexto?.expedient?._id || plan.expedient_id;
  const tareas = contexto?.tareas_abiertas || [];

  // El último hilo de Gmail del deudor, para contestar en el mismo. Lo busca el
  // servidor en todo el historial (`email_thread`); las `interactions` son solo
  // las 12 últimas y no bastan, se usan si el servidor aún no lo manda.
  const hilo = useMemo(() => {
    if (contexto?.email_thread?.gmail_thread_id) return contexto.email_thread;
    const correos = (contexto?.interactions || []).filter(i => i.gmail_thread_id);
    if (!correos.length) return null;
    const ultimo = correos[correos.length - 1];
    return { gmail_thread_id: ultimo.gmail_thread_id, subject: ultimo.asunto?.replace(/^\s*((re|fwd|fw)\s*:\s*)+/i, '') };
  }, [contexto]);

  // En un hilo el asunto no se elige: Gmail exige el del hilo para encadenar, y
  // el servidor lo pondría igual. Solo se escribe cuando el correo abre hilo.
  const asuntoAEnviar = hilo ? `Re: ${hilo.subject || ''}`.trim() : asunto.trim();

  const subir = async (files, carpeta, alTerminar) => {
    setSubiendo(true);
    try {
      alTerminar(await Promise.all(files.map(f => subirArchivo(f, expedientId, carpeta))));
    } catch (err) {
      toast.error(`No se pudo subir el archivo: ${getErrorMessage(err)}`);
    } finally {
      setSubiendo(false);
    }
  };

  // Las anotaciones-tarea pendientes del expediente y el documento por subir.
  const [tareasDeAnotacion, setTareasDeAnotacion] = useState([]);
  const [documento, setDocumento] = useState(null);
  const [nombreDocumento, setNombreDocumento] = useState('');
  useEffect(() => {
    if (!expedientId) return;
    api
      .get(`/annotations/${expedientId}`)
      .then(({ data }) => setTareasDeAnotacion((Array.isArray(data) ? data : []).filter(a => a.tarea === 'Pendiente')))
      .catch(() => setTareasDeAnotacion([]));
  }, [expedientId]);

  const anadirAlEmail = files => {
    const total = [...adjuntosEmail, ...files].reduce((n, f) => n + (f.size || 0), 0);
    if (total > MAX_EMAIL_ATTACHMENTS_BYTES) {
      toast.error(mensajeDemasiadoGrande(total));
      return;
    }
    subir(files, 'email/attachments', subidos => setAdjuntosEmail(prev => [...prev, ...subidos]));
  };

  const mandarWhatsapp = () => {
    const adjunto = adjuntoWhatsapp[0];
    const params = {
      message: whatsapp.trim(),
      ...(adjunto
        ? { attachment: { filename: adjunto.filename, mime_type: adjunto.mime_type, s3_key: adjunto.s3_key } }
        : {}),
    };
    return ejecutar('send_whatsapp', params, () => {
      setWhatsapp('');
      setAdjuntoWhatsapp([]);
    });
  };

  const mandarSms = () => ejecutar('send_sms', { message: sms.trim() }, () => setSms(''));

  const anotar = async (type, params) => {
    const { data } = await api.post(`${basePath}/plans/${plan._id}/manual-actions`, { type, params });
    setHecho(h => [...h, { type, params }]);
    onCambio?.();
    return data.data;
  };

  const ejecutar = async (type, params, limpiar) => {
    setOcupado(true);
    try {
      const r = await anotar(type, params);
      if (r?.result?.aviso) toast.info(r.result.aviso);
      else toast.success('Hecho.');
      limpiar?.();
    } catch (err) {
      const error = err?.response?.data?.error;
      // Una línea en su tiempo de espera no es un «no»: se puede forzar, como
      // en la página de gestión.
      if (error?.puede_forzar && type === 'send_whatsapp') {
        // eslint-disable-next-line no-alert
        if (window.confirm(`${error.message}\n\n¿Enviarlo igualmente?`)) {
          return ejecutar(type, { ...params, force: true }, limpiar);
        }
      } else {
        toast.error(getErrorMessage(err), { autoClose: 8000 });
      }
    } finally {
      setOcupado(false);
    }
    return null;
  };

  /**
   * Lo elegido del expediente entra en la MISMA lista que lo subido del
   * ordenador, y sin repetir: adjuntar dos veces el mismo fichero no da error
   * en ningún sitio y quien lo nota es el deudor.
   */
  const anadirDelExpediente = elegidos =>
    setAdjuntosEmail(prev => {
      const puestos = new Set(prev.map(a => a.s3_key).filter(Boolean));
      return [...prev, ...elegidos.filter(d => !puestos.has(d.s3_key))];
    });

  // El correo sale por la ruta de siempre —la que contesta en el mismo hilo de
  // Gmail o sale por Resend en AR— y después se anota en el plan.
  const mandarEmail = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post(`/expedients/${expedientId}/emails/send`, {
        subject: asuntoAEnviar,
        body: textoAHtml(cuerpo),
        ...(hilo ? { reply_to_thread_id: hilo.gmail_thread_id } : {}),
        ...(adjuntosEmail.length ? { attachments: adjuntosEmail } : {}),
      });
      await anotar('send_email', {
        subject: data?.interaction?.details?.subject || asuntoAEnviar,
        body: cuerpo,
        interaction_id: data?.interaction?._id,
        ...(adjuntosEmail.length ? { attachments: adjuntosEmail.map(a => ({ filename: a.filename })) } : {}),
      });
      toast.success('Email enviado.');
      setAsunto('');
      setCuerpo('');
      setAdjuntosEmail([]);
    } catch (err) {
      toast.error(getErrorMessage(err), { autoClose: 8000 });
    } finally {
      setOcupado(false);
    }
  };

  const subirDocumento = async () => {
    const punto = documento.name.lastIndexOf('.');
    const extension = punto > 0 ? documento.name.slice(punto).toLowerCase() : '';
    const slug = `${slugify(nombreDocumento.trim(), { lower: true, strict: true })}${extension}`;
    setSubiendo(true);
    try {
      const subido = await uploadFileToS3(documento, expedientId, slug, nombreDocumento.trim(), documento.type);
      await ejecutar(
        'attach_document',
        { file_name: nombreDocumento.trim(), slug, file_type: documento.type || undefined, extension, url: subido.url },
        () => {
          setDocumento(null);
          setNombreDocumento('');
        }
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubiendo(false);
    }
  };

  const terminar = async () => {
    const quedaSeguimiento = tareas.some(t => SIGUIENTES.includes(t.task_type));
    if (!quedaSeguimiento) {
      // eslint-disable-next-line no-alert
      const seguir = window.confirm(
        'No queda ningún seguimiento vivo en este expediente: si el deudor no contesta, nadie volverá a mirarlo.\n\n¿Terminar igualmente?'
      );
      if (!seguir) return;
    }
    setOcupado(true);
    try {
      await api.post(`${basePath}/plans/${plan._id}/manual-finish`, { porque: porque.trim() || undefined });
      toast.success('Hecho. El agente aprenderá de lo que has hecho.');
      onTerminado?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOcupado(false);
    }
  };

  const textarea = 'w-full text-sm border border-slate-300 rounded-lg p-2';
  const boton =
    'px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 disabled:opacity-50';

  return (
    <div className='rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-4 space-y-3'>
      <div>
        <p className='text-sm font-semibold text-indigo-900'>Lo haces tú</p>
        <p className='text-xs text-indigo-800'>
          La tarea del agente ya está cerrada a tu nombre. Lo que hagas aquí queda registrado y el agente lo
          aprenderá para casos parecidos.
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
        <Bloque titulo='WhatsApp al deudor'>
          <textarea value={whatsapp} onChange={e => setWhatsapp(e.target.value)} rows={3} className={textarea} />
          <Adjuntos
            archivos={adjuntoWhatsapp}
            onAnadir={files => subir(files.slice(0, 1), 'whatsapp/sent', setAdjuntoWhatsapp)}
            onQuitar={() => setAdjuntoWhatsapp([])}
            disabled={ocupado || subiendo}
            testId='adjunto-whatsapp'
          />
          <p className='text-[11px] text-slate-500 mt-1'>
            Sale por la línea de la conversación. Por las líneas del navegador todavía no se pueden mandar archivos.
          </p>
          <button
            type='button'
            disabled={ocupado || subiendo || (!whatsapp.trim() && adjuntoWhatsapp.length === 0)}
            onClick={mandarWhatsapp}
            className={`${boton} mt-2`}
          >
            Enviar WhatsApp
          </button>
        </Bloque>

        <Bloque titulo='SMS al deudor'>
          <textarea
            value={sms}
            onChange={e => setSms(e.target.value)}
            rows={3}
            className={textarea}
            aria-label='SMS al deudor'
          />
          <p className={`text-[11px] mt-1 ${sms.trim().length > MAX_SMS ? 'text-red-600' : 'text-slate-500'}`}>
            {sms.trim().length}/{MAX_SMS}. Sale desde el número de BulkGate; si contesta, entra como respuesta de SMS.
          </p>
          <button
            type='button'
            disabled={ocupado || !sms.trim() || sms.trim().length > MAX_SMS}
            onClick={mandarSms}
            className={`${boton} mt-2`}
          >
            Enviar SMS
          </button>
        </Bloque>

        <Bloque titulo='Email al deudor'>
          {hilo ? (
            <p className='text-sm text-slate-700 mb-2 truncate' title={asuntoAEnviar}>
              {asuntoAEnviar}
            </p>
          ) : (
            <input
              value={asunto}
              onChange={e => setAsunto(e.target.value)}
              placeholder='Asunto'
              className={`${textarea} mb-2`}
            />
          )}
          <textarea
            value={cuerpo}
            onChange={e => setCuerpo(e.target.value)}
            rows={3}
            placeholder='Mensaje del email'
            className={textarea}
          />
          <Adjuntos
            archivos={adjuntosEmail}
            onAnadir={anadirAlEmail}
            onQuitar={i => setAdjuntosEmail(prev => prev.filter((_, j) => j !== i))}
            multiple
            disabled={ocupado || subiendo}
            testId='adjuntos-email'
          />
          <button
            type='button'
            onClick={() => setVerDocumentos(v => !v)}
            disabled={ocupado || subiendo}
            className='inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50 mt-1'
          >
            <DocumentTextIcon className='w-4 h-4' />
            Del expediente
          </button>
          {verDocumentos && (
            <SelectorDocumentosExpediente
              expedientId={expedientId}
              yaAdjuntos={adjuntosEmail}
              onElegir={anadirDelExpediente}
              onCerrar={() => setVerDocumentos(false)}
            />
          )}
          <p className='text-[11px] text-slate-500 mt-1'>
            {hilo ? 'Contesta en el último hilo del deudor, con su asunto.' : 'No hay correos previos: abre un hilo nuevo.'}
          </p>
          <button
            type='button'
            disabled={ocupado || subiendo || !asuntoAEnviar || !cuerpo.trim()}
            onClick={mandarEmail}
            className={`${boton} mt-2`}
          >
            Enviar email
          </button>
        </Bloque>

        <Bloque titulo='Estado del expediente'>
          <div className='flex gap-2'>
            <select
              value={estado}
              onChange={e => setEstado(e.target.value)}
              className='flex-1 text-sm border border-slate-300 rounded-lg p-2'
            >
              <option value=''>— Ahora: {contexto?.expedient?.estado || '?'} —</option>
              {MANUALLY_SETTABLE_STATES.map(s => (
                <option key={s} value={s}>
                  {expedientStatusLabel(s)}
                </option>
              ))}
            </select>
            <button
              type='button'
              disabled={ocupado || !estado}
              onClick={() => ejecutar('update_expedient_status', { new_status: estado }, () => setEstado(''))}
              className={boton}
            >
              Cambiar
            </button>
          </div>
        </Bloque>

        <Bloque titulo='Anotación'>
          <textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            rows={2}
            placeholder='Texto de la anotación'
            className={textarea}
          />
          <label className='flex items-center gap-2 text-sm text-slate-700 mt-1'>
            <input type='checkbox' checked={esTarea} onChange={e => setEsTarea(e.target.checked)} />
            Tarea para el cliente
          </label>
          <button
            type='button'
            disabled={ocupado || !nota.trim()}
            onClick={() =>
              ejecutar('create_annotation', { text: nota.trim(), es_tarea: esTarea }, () => {
                setNota('');
                setEsTarea(false);
              })
            }
            className={`${boton} mt-2`}
          >
            Añadir anotación
          </button>
        </Bloque>

        {/* Las anotaciones con tarea (no los assignments): se completan desde aquí,
            sin ir a la ficha (23/10/2026). */}
        <Bloque titulo='Anotaciones con tarea'>
          {tareasDeAnotacion.length === 0 ? (
            <p className='text-sm text-slate-500'>No hay ninguna pendiente.</p>
          ) : (
            <ul className='space-y-1'>
              {tareasDeAnotacion.map(a => (
                <li key={a._id} className='flex items-start justify-between gap-2 text-sm'>
                  <span className='text-slate-700'>
                    {a.anotacion}
                    <span className='text-slate-400'> · {new Date(a.fecha).toLocaleDateString('es-ES')}</span>
                  </span>
                  <button
                    type='button'
                    disabled={ocupado}
                    onClick={() =>
                      ejecutar('complete_annotation_task', { annotation_id: a._id }, () =>
                        setTareasDeAnotacion(prev => prev.filter(x => x._id !== a._id))
                      )
                    }
                    className='shrink-0 text-xs text-emerald-700 hover:text-emerald-900 disabled:opacity-50'
                  >
                    Completar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Bloque>

        {/* Un documento a los documentos del expediente, como desde la ficha. */}
        <Bloque titulo='Documentos del expediente'>
          <input
            type='file'
            aria-label='Documento para el expediente'
            onChange={e => setDocumento(e.target.files?.[0] || null)}
            className='text-sm'
          />
          <input
            value={nombreDocumento}
            onChange={e => setNombreDocumento(e.target.value)}
            placeholder='Nombre del documento'
            className={`${textarea} mt-2`}
          />
          <button
            type='button'
            disabled={ocupado || subiendo || !documento || !nombreDocumento.trim()}
            onClick={subirDocumento}
            className={`${boton} mt-2`}
          >
            Subir al expediente
          </button>
        </Bloque>

        <Bloque titulo='Tareas'>
          {tareas.length === 0 ? (
            <p className='text-sm text-slate-500 mb-2'>No hay ninguna abierta.</p>
          ) : (
            <ul className='space-y-1 mb-3'>
              {tareas.map(t => (
                <li key={t._id} className='flex items-center justify-between gap-2 text-sm'>
                  <span className='text-slate-700'>
                    {TASK_TYPE_LABELS[t.task_type] || t.task_type}
                    <span className='text-slate-400'> · {new Date(t.date).toLocaleDateString('es-ES')}</span>
                  </span>
                  <button
                    type='button'
                    disabled={ocupado}
                    onClick={() => ejecutar('cancel_assignment', { assignment_id: t._id })}
                    className='text-xs text-red-600 hover:text-red-800 disabled:opacity-50'
                  >
                    Cancelar
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className='grid grid-cols-2 gap-2'>
            <select
              value={tipoTarea}
              onChange={e => setTipoTarea(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg p-2'
            >
              {tiposDeTarea.map(t => (
                <option key={t} value={t}>
                  {TASK_TYPE_LABELS[t] || t}
                </option>
              ))}
            </select>
            <input
              type='date'
              value={fechaTarea}
              min={hoyISO()}
              onChange={e => setFechaTarea(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg p-2'
            />
          </div>
          <input
            value={descTarea}
            onChange={e => setDescTarea(e.target.value)}
            placeholder='Qué hay que hacer'
            className={`${textarea} mt-2`}
          />
          <button
            type='button'
            disabled={ocupado || !descTarea.trim() || !fechaTarea}
            onClick={() =>
              ejecutar(
                'create_follow_up_assignment',
                { task_type: tipoTarea, days_from_now: diasHasta(fechaTarea), description: descTarea.trim() },
                () => setDescTarea('')
              )
            }
            className={`${boton} mt-2`}
          >
            Crear tarea
          </button>
        </Bloque>

        <Bloque titulo='Hecho en este caso'>
          {hecho.length === 0 ? (
            <p className='text-sm text-slate-500'>Todavía nada.</p>
          ) : (
            <ul className='list-disc pl-4 text-sm text-slate-700 space-y-0.5'>
              {hecho.map((h, i) => (
                <li key={i}>{(QUE_HICE[h.type] || (() => h.type))(h.params)}</li>
              ))}
            </ul>
          )}
        </Bloque>
      </div>

      <div className='rounded-lg border border-slate-200 bg-white p-3'>
        <label className='block text-xs font-semibold text-slate-500 uppercase mb-1'>
          ¿Por qué? <span className='normal-case font-normal text-slate-400'>(opcional)</span>
        </label>
        <input
          value={porque}
          onChange={e => setPorque(e.target.value)}
          placeholder='Lo que el agente debería haber tenido en cuenta'
          className={textarea}
        />
        <button
          type='button'
          disabled={ocupado}
          onClick={terminar}
          className='mt-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50'
        >
          Terminar
        </button>
      </div>
    </div>
  );
};

export default AIAgentLoHagoYo;
